/**
 * Desteklenen diller. Kaynak dil İngilizce: çeviriler ondan yapılır, anahtar tipleri ondan
 * türetilir. Yeni dil = ilgili listeye bir satır + o listenin ad alanlarında bir JSON dosyası
 * (katalog testi eksik anahtarı yakalar).
 *
 * İKİ LİSTE var, çünkü iki ayrı izleyici var:
 *
 *   - `LOCALES` — işletmenin gördüğü yüzeyler: panel, e-posta, bildirim. TR + EN
 *     (MIMARI §2.10). Panel tek başına 1.500'ün üzerinde anahtar taşıyor; yeni bir dil,
 *     o anahtarların tamamının çevrilmesi demek.
 *   - `VISITOR_LOCALES` — müşterinin gördüğü yüzeyler: widget ve balon (M1 §3.12.8) ve
 *     2026-10-08'den beri TANITIM SİTESİ. Sohbet balonunu satın alan işletmenin müşterisi
 *     panelin dilini değil kendi dilini konuşuyor; siteye gelen aday müşteri de öyle — site
 *     ürünün yüzü ve iki dilde kalması, on dilde satış yapmamak demekti.
 *
 * Tek liste tutulsaydı iki sonuçtan biri çıkardı: ya widget 2 dile hapsolurdu ya da panelin
 * 1.500 anahtarı 12 dile çevrilmeden hiçbir dil eklenemezdi.
 */
export const LOCALES = ['en', 'tr'] as const;
export type Locale = (typeof LOCALES)[number];
export const SOURCE_LOCALE: Locale = 'en';

/** Widget arayüz dilleri (M1 §3.12.8, D1). Panel dilleri bunun alt kümesidir. */
export const VISITOR_LOCALES = [
  'ar',
  'az',
  'de',
  'en',
  'es',
  'fr',
  'it',
  'nl',
  'pl',
  'pt',
  'ru',
  'tr',
] as const;
export type VisitorLocale = (typeof VISITOR_LOCALES)[number];

/**
 * Ürünün tanıdığı herhangi bir dil. Panel dilleri ziyaretçi dillerinin alt kümesi olduğu için
 * birleşim `VisitorLocale`a eşit; ad, çeviri altyapısının hangi listeye ait olduğunu bilmeden
 * çalıştığı yerlerde (çevirmen, katalog) niyeti okunur kılıyor.
 */
export type AnyLocale = VisitorLocale;

/**
 * Ziyaretçiye görünen ad alanları (katalog adları). Kalan ad alanları panel dillerini taşıyor.
 * `site` burada: tanıtım sitesi de hesabı olmayan birine konuşuyor ve o kişinin dili, panelin
 * iki diliyle sınırlı değil.
 */
export const VISITOR_NAMESPACES = ['widget', 'launcher', 'surface', 'site'] as const;

/**
 * Bir ad alanının taşımak ZORUNDA olduğu diller. Katalog bütünlük denemesi ve çalışma zamanı
 * aynı kaynaktan okuyor: liste iki yere yazılsaydı, biri güncellenip öteki unutulduğunda
 * eksik çeviri denemeden kaçardı.
 */
export const localesOf = (namespace: string): readonly AnyLocale[] =>
  (VISITOR_NAMESPACES as readonly string[]).includes(namespace) ? VISITOR_LOCALES : LOCALES;

/** Sağdan sola yazılan diller. */
const RTL_LOCALES: ReadonlySet<string> = new Set(['ar', 'he', 'fa', 'ur']);

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

export function isVisitorLocale(value: string): value is VisitorLocale {
  return (VISITOR_LOCALES as readonly string[]).includes(value);
}

export function textDirection(locale: AnyLocale): 'ltr' | 'rtl' {
  return RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
}

/**
 * Tercih listesinden (tarayıcı dilleri, Accept-Language, kullanıcı ayarı) desteklenen ilk dili
 * seçer: önce tam eşleşme (`pt-BR`), sonra ana dil (`pt`). Hiçbiri yoksa `fallback`.
 *
 * Kendi kendine yeter: modül düzeyinde hiçbir şeye dokunmaz. Sitenin boyamadan önce çalışan dil
 * betiğine metin olarak gömülür (tek eşleştirme kuralı, iki kopya yok).
 */
export function pickLocale<L extends string>(
  preferences: readonly (string | null | undefined)[],
  supported: readonly L[],
  fallback: L,
): L {
  for (const preference of preferences) {
    if (!preference) continue;
    const normalized = preference.trim().toLowerCase();
    const base = normalized.split('-')[0];
    const match =
      supported.find((candidate) => candidate === normalized) ??
      supported.find((candidate) => candidate === base);
    if (match !== undefined) return match;
  }
  return fallback;
}

/** Desteklenen dillerden, yoksa kaynak dil. */
export const negotiateLocale = (preferences: readonly (string | null | undefined)[]): Locale =>
  pickLocale(preferences, LOCALES, SOURCE_LOCALE);

/**
 * Ziyaretçinin dili: seçim sırası M1 §3.12.8'de — ziyaretçinin seçimi → `data-locale` →
 * sayfanın `lang`ı → tarayıcı dili → varsayılan. Sıralamayı çağıran kurar; burası yalnız
 * eşleştirir.
 */
export const negotiateVisitorLocale = (
  preferences: readonly (string | null | undefined)[],
): VisitorLocale => pickLocale(preferences, VISITOR_LOCALES, SOURCE_LOCALE);
